import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Mail, Phone, ArrowRight, FileText, Loader2, KeyRound } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { api } from "@/lib/api";

export default function ConfirmationPage() {
  const { enrollmentId } = useParams();
  const [enrollment, setEnrollment] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/enroll/${enrollmentId}`).then((r) => r.data),
      api.get(`/settings`).then((r) => r.data),
    ])
      .then(([e, s]) => {
        setEnrollment(e);
        setSettings(s);
      })
      .catch(() => setError(true));
  }, [enrollmentId]);

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <div className="max-w-2xl mx-auto px-5 py-24 text-center">
          <h1 className="h-serif text-3xl text-purple-dark mb-4">
            Enrollment not found
          </h1>
          <p className="text-ink/70">
            We couldn't locate an enrollment with that ID.
          </p>
          <Link
            to="/"
            className="inline-block mt-6 text-purple hover:text-purple-dark link-underline"
          >
            Return home
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (!enrollment) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="animate-spin text-purple" size={32} />
      </div>
    );
  }

  const paymentInstructions =
    enrollment.payment_method === "cash_check_now"
      ? settings?.payment_instructions_cash
      : settings?.payment_instructions_plan;

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      <section className="bg-purple-dark text-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 text-center">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-gold text-ink mb-6">
            <Check size={26} strokeWidth={3} />
          </div>
          <div className="text-xs tracking-[0.22em] uppercase text-gold-light font-bold mb-3">
            Application received
          </div>
          <h1 className="h-serif text-4xl sm:text-5xl tracking-tight mb-4">
            Thank you for applying.
          </h1>
          <p className="text-white/85 leading-relaxed max-w-xl mx-auto">
            {enrollment.parent_first_name}, thank you for beginning the
            enrollment process with Zion Christian Academy. Our director will
            personally review your application and be in touch soon. A
            confirmation email is on its way to{" "}
            <strong className="text-gold-light">{enrollment.email}</strong>.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
        <div className="border border-grey">
          <div className="p-6 sm:p-8 border-b border-grey">
            <div className="grid sm:grid-cols-3 gap-6">
              <Info
                label="Enrollment ID"
                value={enrollment.enrollment_id}
                mono
                testId="conf-enrollment-id"
              />
              <Info
                label="School Year"
                value={enrollment.school_year}
                testId="conf-school-year"
              />
              <Info
                label="Students"
                value={`${enrollment.student_count} enrolled`}
                testId="conf-student-count"
              />
            </div>
          </div>

          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <FileText size={18} className="text-gold" />
              <h2 className="h-serif text-xl text-purple-dark">
                Payment instructions (submit after approval)
              </h2>
            </div>
            <p className="text-sm text-ink/85 leading-relaxed whitespace-pre-line">
              {paymentInstructions}
            </p>

            <div className="mt-6 border-t border-grey pt-6 text-xs tracking-[0.15em] uppercase text-purple-light font-bold mb-3">
              What happens next
            </div>
            <ol className="space-y-3 text-sm text-ink/85 list-decimal ml-5">
              <li>
                Our director will personally review your application — usually
                within 1–2 business days — and reach out to welcome your family.
              </li>
              <li>
                Once approved, we'll confirm your payment (cash or check per the
                instructions above) or finalize your payment plan.
              </li>
              <li>
                After approval and payment, your three enrollment documents —
                Enrollment Form, Welcome Letter, and BOE Instructions — will be
                emailed to you and available in your parent portal.
              </li>
              <li>
                You'll use the BOE Instructions letter to notify your county
                Board of Education (only required if withdrawing from a public
                school).
              </li>
            </ol>
          </div>
        </div>

        <div className="mt-8 border border-gold bg-gold/5 p-6 sm:p-7" data-testid="portal-callout">
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-full bg-gold text-ink flex items-center justify-center shrink-0">
              <KeyRound size={20} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] tracking-[0.22em] uppercase text-purple-light font-bold mb-1">
                Your parent portal is ready
              </div>
              <h3 className="h-serif text-xl text-purple-dark mb-2">
                Sign in with your Enrollment ID
              </h3>
              <p className="text-sm text-ink/85 leading-relaxed mb-4">
                Once Director Brady confirms your payment, your official
                enrollment documents will unlock in your parent portal — no
                need to wait for another email. Use your{" "}
                <b className="font-mono text-purple">{enrollment.enrollment_id}</b>{" "}
                and the password you set to sign in any time.
              </p>
              <Link
                to="/portal/login"
                className="inline-flex items-center gap-2 bg-purple text-white hover:bg-purple-dark font-bold px-5 py-2.5 rounded-sm text-sm transition-colors"
                data-testid="conf-portal-btn"
              >
                Open Parent Portal <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          <a
            href={`mailto:${settings?.contact_email || "zca.k.12.26@outlook.com"}`}
            className="inline-flex items-center justify-between bg-white border border-purple text-purple hover:bg-grey-light px-6 py-4 transition-colors group"
            data-testid="conf-email-btn"
          >
            <span className="flex items-center gap-3">
              <Mail size={18} />
              <span className="font-bold text-sm">Email the school</span>
            </span>
            <ArrowRight
              size={16}
              className="transition-transform group-hover:translate-x-1"
            />
          </a>
          <a
            href={`tel:${(settings?.contact_phone || "(205) 200-6257").replace(/[^\d+]/g, "")}`}
            className="inline-flex items-center justify-between bg-purple text-white hover:bg-purple-dark px-6 py-4 transition-colors group"
            data-testid="conf-phone-btn"
          >
            <span className="flex items-center gap-3">
              <Phone size={18} />
              <span className="font-bold text-sm">{settings?.contact_phone || "(205) 200-6257"}</span>
            </span>
            <ArrowRight
              size={16}
              className="transition-transform group-hover:translate-x-1"
            />
          </a>
        </div>

        <p className="text-xs text-ink/60 text-center mt-10 max-w-md mx-auto italic h-serif">
          "Bring them up in the training and instruction of the Lord."
          <span className="not-italic font-sans"> — Ephesians 6:4</span>
        </p>
      </section>

      <SiteFooter />
    </div>
  );
}

function Info({ label, value, mono, testId }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.22em] uppercase text-purple-light font-bold mb-1.5">
        {label}
      </div>
      <div
        className={`text-lg text-purple-dark ${
          mono ? "font-mono tracking-wide" : "h-serif"
        }`}
        data-testid={testId}
      >
        {value}
      </div>
    </div>
  );
}
